// Klienten mot API:et.
//
// Access-token hålls medvetet i en vanlig variabel och inte i localStorage.
// Allt som ligger i localStorage kan läsas av vilket skript som helst på sidan,
// och en token som överlever en omladdning är en token som kan stjälas i lugn
// och ro. Den här försvinner när fliken stängs — och det gör inget, eftersom
// refresh-cookien (som JavaScript inte kan läsa) hämtar en ny.

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export class ApiError extends Error {
  constructor(status, message, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function parseError(res) {
  const body = await res.json().catch(() => ({}));
  return new ApiError(
    res.status,
    body.error || `Något gick fel (${res.status}).`,
    body.code
  );
}

// Ett pågående refresh-anrop delas av alla som frågar samtidigt.
//
// Utan detta blir rotationen självdestruktiv: två samtidiga anrop skickar
// samma cookie, det första roterar den och ogiltigförklarar den gamla, och det
// andra kommer med en token som just dödats och får 401. Det räcker med två
// flikar, eller två API-anrop vars access-token gick ut samtidigt.
let pagaendeRefresh = null;

/// Hämtar en ny access-token med refresh-cookien. Returnerar true om det gick.
function refresh() {
  if (pagaendeRefresh) return pagaendeRefresh;

  pagaendeRefresh = (async () => {
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        accessToken = null;
        return false;
      }
      const data = await res.json();
      accessToken = data.accessToken;
      return true;
    } finally {
      pagaendeRefresh = null;
    }
  })();

  return pagaendeRefresh;
}

async function request(path, { method = "GET", body, retry = true } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: {
      ...(body ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (res.ok) {
    return res.status === 204 ? null : res.json();
  }

  const err = await parseError(res);

  // Utgången access-token är väntat och ska inte synas för användaren: hämta en
  // ny och gör om anropet en gång. Misslyckas även det är sessionen slut.
  if (err.status === 401 && err.code === "TOKEN_EXPIRED" && retry) {
    if (await refresh()) {
      return request(path, { method, body, retry: false });
    }
  }

  throw err;
}

export const api = {
  // --- Inloggning ---
  login: (email, password) =>
    request("/auth/login", { method: "POST", body: { email, password } }),
  register: (data) => request("/auth/register", { method: "POST", body: data }),
  logout: () => request("/auth/logout", { method: "POST" }),
  me: () => request("/auth/me"),
  /// Personer ägaren kan agera som.
  viewAsTargets: () => request("/auth/view-as/targets"),
  /// Byter vilket konto ägaren agerar i. userId: null = tillbaka till sig själv.
  viewAs: (userId) => request("/auth/view-as", { method: "POST", body: { userId } }),
  /// Begär en återställningslänk. Svaret är alltid detsamma, oavsett om
  /// adressen finns — det avslöjar inte vilka konton som existerar.
  forgotPassword: (email) =>
    request("/auth/forgot", { method: "POST", body: { email } }),
  lookupReset: (token) => request(`/auth/reset/${token}`),
  resetPassword: (token, password) =>
    request(`/auth/reset/${token}`, { method: "POST", body: { password } }),
  changePassword: (currentPassword, newPassword) =>
    request("/auth/password", { method: "POST", body: { currentPassword, newPassword } }),
  resetStaffPassword: (staffId, newPassword) =>
    request(`/staff/${staffId}/password`, { method: "POST", body: { newPassword } }),
  resetContactPassword: (companyId, contactId, newPassword) =>
    request(`/customers/${companyId}/contacts/${contactId}/password`, {
      method: "POST",
      body: { newPassword },
    }),

  /// Försöker återuppta en session vid sidladdning. Returnerar null om det inte
  /// finns någon giltig refresh-cookie, vilket är det normala för en utloggad.
  async restoreSession() {
    if (!(await refresh())) return null;
    return request("/auth/me");
  },

  // --- Ordrar ---
  orders: () => request("/orders"),
  order: (id) => request(`/orders/${id}`),
  createOrder: (data) => request("/orders", { method: "POST", body: data }),
  updateOrder: (id, data) => request(`/orders/${id}`, { method: "PATCH", body: data }),
  deleteOrder: (id) => request(`/orders/${id}`, { method: "DELETE" }),

  // --- Timern ---
  startStep: (orderId, stepId) =>
    request(`/orders/${orderId}/steps/${stepId}/start`, { method: "POST" }),
  pauseStep: (orderId, stepId) =>
    request(`/orders/${orderId}/steps/${stepId}/pause`, { method: "POST" }),
  completeStep: (orderId, stepId) =>
    request(`/orders/${orderId}/steps/${stepId}/complete`, { method: "POST" }),
  setStepMinutes: (orderId, stepId, minutes) =>
    request(`/orders/${orderId}/steps/${stepId}/minutes`, {
      method: "PATCH",
      body: { minutes },
    }),

  // --- Bokning, chatt och kvittens ---
  setOrderStatus: (orderId, status) =>
    request(`/orders/${orderId}/status`, { method: "PATCH", body: { status } }),
  sendMessage: (orderId, text) =>
    request(`/orders/${orderId}/messages`, { method: "POST", body: { text } }),
  markMessagesRead: (orderId) =>
    request(`/orders/${orderId}/messages/read`, { method: "POST" }),
  kvittera: (orderId) =>
    request(`/orders/${orderId}/kvittens`, { method: "POST" }),

  // --- Personal ---
  staff: () => request("/staff"),
  createStaff: (data) => request("/staff", { method: "POST", body: data }),
  updateStaff: (id, data) =>
    request(`/staff/${id}`, { method: "PATCH", body: data }),

  // --- Schema och frånvaro ---
  schedule: () => request("/schedule"),
  addScheduleEntry: (data) => request("/schedule", { method: "POST", body: data }),
  removeScheduleEntry: (id) => request(`/schedule/${id}`, { method: "DELETE" }),
  publishWeek: (weekStart) =>
    request("/schedule/publish", { method: "POST", body: { weekStart } }),
  unpublishWeek: (weekStart) =>
    request("/schedule/unpublish", { method: "POST", body: { weekStart } }),

  absences: () => request("/absences"),
  addAbsence: (data) => request("/absences", { method: "POST", body: data }),
  removeAbsence: (id) => request(`/absences/${id}`, { method: "DELETE" }),

  // --- Meddelanden ---
  announcements: () => request("/announcements"),
  addAnnouncement: (data) => request("/announcements", { method: "POST", body: data }),
  markAnnouncementsRead: () => request("/announcements/read", { method: "POST" }),

  customerMessages: () => request("/customer-messages"),
  addCustomerMessage: (data) =>
    request("/customer-messages", { method: "POST", body: data }),
  markCustomerMessagesRead: () =>
    request("/customer-messages/read", { method: "POST" }),

  meetings: () => request("/meetings"),
  addMeeting: (data) => request("/meetings", { method: "POST", body: data }),
  removeMeeting: (id) => request(`/meetings/${id}`, { method: "DELETE" }),
  markMeetingsSeen: () => request("/meetings/seen", { method: "POST" }),

  // --- Kundkonton ---
  customers: () => request("/customers"),
  setCustomerStatus: (id, status) =>
    request(`/customers/${id}/status`, { method: "PATCH", body: { status } }),
  removeCustomer: (id) => request(`/customers/${id}`, { method: "DELETE" }),
  addContact: (id, data) =>
    request(`/customers/${id}/contacts`, { method: "POST", body: data }),
  removeContact: (id, contactId) =>
    request(`/customers/${id}/contacts/${contactId}`, { method: "DELETE" }),

  // --- Inbjudningar ---
  invites: () => request("/invites"),
  createInvite: (data) => request("/invites", { method: "POST", body: data }),
  cancelInvite: (id) => request(`/invites/${id}`, { method: "DELETE" }),
  /// Öppna delen — den inbjudna är inte inloggad.
  lookupInvite: (token) => request(`/invite/${token}`),
  acceptInvite: (token, password) =>
    request(`/invite/${token}/accept`, { method: "POST", body: { password } }),

  // --- Inställningar ---
  updateSettings: (data) => request("/settings", { method: "PATCH", body: data }),
};

// Serverns klocka minus vår egen, mätt vid inloggning. Används för att räkna ut
// hur länge en timer gått utan att en felställd dator visar fel siffra.
let clockOffset = 0;

export function syncClock(serverNow) {
  if (typeof serverNow === "number") {
    clockOffset = serverNow - Date.now();
  }
}

export function serverTime() {
  return Date.now() + clockOffset;
}
